import { useState, useEffect, useRef } from 'react';
import { m } from 'framer-motion';
import { Round, GameState, Player } from '@brainfart/shared';
import { ScoreChip } from './ScoreChip';
import { ScreenHeader } from './ScreenHeader';
import { LeaveGame } from './LeaveGame';
import { PromptText } from './PromptText';
import { WRONG_ANSWER_LINES, pickRandom } from '../flavorText';
import { playCorrect, playWrong } from '../sound';
import { ReportButton } from './ReportQuestion';

interface RevealScreenProps {
  round: Round;
  game: GameState;
  me: Player;
  onLeave: () => void;
}

function formatCorrectAnswer(ca: string | number | string[] | undefined): string {
  if (ca === undefined || ca === null) return '';
  if (Array.isArray(ca)) return ca.join(' or ');
  return String(ca);
}

// After each question: the right answer, then one compact row per player as
// their answer flips over (the server staggers them).
export function RevealScreen({ round, game, me, onLeave }: RevealScreenProps) {
  const question = round.question;
  const revealedAnswers = Object.entries(round.answers);
  const currentRevealIndex = revealedAnswers.length - 1;

  const expectedCount = game.players.filter(p => p.hasAnswered).length;
  const allRevealed = expectedCount > 0 && revealedAnswers.length >= expectedCount;

  const isTriviaWithAnswer = question.type !== 'order' && question.correctAnswer !== undefined;
  const allWrong = isTriviaWithAnswer && allRevealed && revealedAnswers.every(([playerId]) => round.correctness[playerId] === false);

  // Rounds finish themselves server-side once every row has flipped, so
  // there's no client timer here: exactly one pause in the pipeline.

  // Play a correct/wrong sting once my own answer is revealed this round.
  const soundedRef = useRef(false);
  const myCorrect = round.correctness ? round.correctness[me.id] : undefined;
  useEffect(() => {
    if (soundedRef.current || myCorrect === undefined) return;
    soundedRef.current = true;
    if (myCorrect) playCorrect(); else playWrong();
  }, [myCorrect]);

  // One kind line for me when I missed it, not one per player.
  const [wrongLine] = useState(() => pickRandom(WRONG_ANSWER_LINES));

  return (
    <m.div
      className="reveal-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <ScreenHeader
        left={<span style={{ color: '#888', fontSize: '0.85rem', fontWeight: 600 }}>Round {game.currentRound + 1} / {game.settings.rounds}</span>}
        right={<ScoreChip players={game.players} meId={me.id} />}
      />

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1.25rem 1.25rem 6.5rem', width: '100%', maxWidth: 640, margin: '0 auto' }}>
        <h1 style={{ fontSize: 'clamp(1.15rem, 4vw, 1.5rem)', fontWeight: 700, lineHeight: 1.3, textAlign: 'center', marginBottom: '1rem' }}><PromptText prompt={question.prompt} /></h1>

        {/* The right answer, always: you never leave a round without learning it. */}
        {isTriviaWithAnswer && allRevealed && (
          <m.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              width: '100%', textAlign: 'center', padding: '0.75rem 1rem', marginBottom: '0.5rem',
              background: allWrong ? 'rgba(255,77,106,0.1)' : 'rgba(57,255,136,0.1)',
              border: `1px solid ${allWrong ? 'rgba(255,77,106,0.3)' : 'rgba(57,255,136,0.35)'}`,
              borderRadius: '14px', color: allWrong ? '#ff8a95' : '#8affc0', fontWeight: 600,
            }}
          >
            {allWrong ? '😅 Nobody got it. The answer was ' : '✅ Correct answer: '}
            <strong style={{ color: '#fff' }}>{formatCorrectAnswer(question.correctAnswer)}</strong>
          </m.div>
        )}
        {isTriviaWithAnswer && allRevealed && (
          <ReportButton target={{ questionId: question.id, prompt: question.prompt, mode: 'multiplayer', picked: round.answers[me.id] ?? null }} />
        )}

        <ul style={{ listStyle: 'none', margin: '0.75rem 0 0', padding: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {revealedAnswers.map(([playerId, answer], idx) => {
            const player = game.players.find(p => p.id === playerId);
            if (!player) return null;
            return (
              <AnswerRow
                key={playerId}
                player={player}
                answer={answer}
                isCorrect={round.correctness && player.id in round.correctness ? round.correctness[player.id] : null}
                isCurrent={idx === currentRevealIndex && !allRevealed}
                isMine={playerId === me.id}
                note={playerId === me.id && round.correctness?.[me.id] === false ? wrongLine : null}
              />
            );
          })}
        </ul>

        {revealedAnswers.length === 0 && round.state !== 'results' && (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#777' }}>Collecting answers…</p>
        )}
        {/* Nobody answered this one: nothing to reveal, just move on */}
        {revealedAnswers.length === 0 && round.state === 'results' && (
          <p style={{ textAlign: 'center', padding: '2rem', color: '#777' }}>😴 Nobody answered this one. Skipping ahead…</p>
        )}

        <div style={{ marginTop: '1.5rem' }}><LeaveGame onLeave={onLeave} /></div>
      </main>
    </m.div>
  );
}

function AnswerRow({ player, answer, isCorrect, isCurrent, isMine, note }: {
  player: Player;
  answer: string;
  isCorrect: boolean | null;
  isCurrent: boolean;
  isMine: boolean;
  note: string | null;
}) {
  return (
    <m.li
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', damping: 20, stiffness: 220 }}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 0.9rem', borderRadius: '14px', background: '#1B1830',
        border: `2px solid ${isCurrent ? '#FFC93C' : isMine ? 'rgba(255,201,60,0.45)' : '#2E2B45'}`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 40, height: 40, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem',
          background: `linear-gradient(135deg, ${player.color}33, ${player.color}11)`, border: `2px solid ${player.color}`,
        }}
      >
        {player.avatar}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: '0.8rem', color: '#9a97b3', fontWeight: 600 }}>
          {player.name}{isMine && <span style={{ color: '#666' }}> (you)</span>}
        </span>
        <span style={{ display: 'block', fontSize: '1rem', lineHeight: 1.35, wordBreak: 'break-word' }}>{answer || '(no answer)'}</span>
        {note && <span style={{ display: 'block', fontSize: '0.75rem', color: '#888', fontStyle: 'italic', marginTop: '0.15rem' }}>{note}</span>}
      </span>
      {isCorrect !== null && (
        <m.span
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 12, stiffness: 160, delay: 0.15 }}
          role="img"
          aria-label={isCorrect ? 'Correct' : 'Incorrect'}
          style={{ flexShrink: 0, fontSize: '1.5rem', fontWeight: 800, color: isCorrect ? '#39FF88' : '#FF4D6A' }}
        >
          {isCorrect ? '✓' : '✗'}
        </m.span>
      )}
    </m.li>
  );
}
